import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useSearchParams } from "react-router-dom";
import type { IdentityProvider } from "@makit/shared";
import {
  ANONYMOUS_SESSION,
  CUTOUT_UNIT_PRICE_KRW,
  IDENTITY_PROVIDER_LABEL,
  LOGIN_REASON_PARAM,
  RECOMMENDED_IMAGE_COUNT,
  VAT_INCLUDED,
  WEB_ENABLED_IDENTITY_PROVIDERS,
  cutoutPrice,
  formatKrw,
} from "@makit/shared";

import { BrandMark } from "../components/primitives";
import { loginTarget, useSession } from "../app/SessionGate";
import { fetchSession, loginStartUrl, reviewLogin, type ReviewLoginResult } from "../lib/api";
import { useDocumentTitle } from "./public/PublicLayout";
import { PUBLIC_PATHS } from "./public/paths";

/** 공급자에서 돌아오지 못했을 때의 **사유 줄 셋** (PRD `7.6.1` 확정, `A-01`도 같은 문구).
 *
 * ⚠️ **공급자 이름이 이 세 문장에 없다. 2026-09-10에 `네이버` 낱말만 걷었다**
 * (`design-manager` 확정. 어순도 다른 낱말도 그대로다). **걷은 이유가 카카오가 늘어서가
 * 아니다.** 이름을 넣으면 **한 상태에 문장이 둘** 생긴다 — 공급자를 아는 경우와 못 싣는
 * 경우(주소를 손으로 고친 경우)가 갈리고 그때 쓸 문장이 따로 필요해진다. `7.5.9`가 막는
 * 것이 그 일이다. **서버 계약은 그대로다** — `reason`은 여전히 셋이고 여기에 공급자를
 * 실어 보내지 않는다. 웹은 최상위 이동이라 **돌아왔을 때 어느 버튼이었는지를 잃는다.**
 * 그 사실이 이 판단의 근거 절반이다.
 *
 * **별도 실패 화면을 만들지 않는다.** 실패한 셀러가 할 일은 "다시 누르는 것" 하나뿐이고
 * 그 버튼이 있는 화면이 여기다. 별도 화면을 만들면 버튼을 한 번 더 눌러 여기로 와야 한다.
 *
 * **셋을 가르는 기준은 원인이 아니라 셀러가 할 수 있는 일이다.** 그만둔 것은 본인이 한 일,
 * 거부는 본인이 되돌릴 수 있는 일, 오류는 기다리는 것 말고 할 게 없다. **한 문구로 뭉치면
 * 셀러는 자기 잘못인지 우리 잘못인지 모르고, 오류일 때 자기 계정을 의심한다.**
 *
 * 둘째 줄이 그 자리에서 수집 항목을 다시 말하는 이유: 거부는 "뭘 가져가려는 거지"에서 나오는
 * 동작이라, **`두 가지만 받습니다`** 라고 답하지 않으면 같은 이유로 또 거부한다.
 *
 * ⚠️ **구현 값을 화면에 흘리지 않는다.** `access_denied`·`invalid_state` 같은 말은 셀러가
 * 보는 자리에 나오지 않는다. `state` 불일치·만료는 셋째 줄로 묶인다 — 할 수 있는 일이 같다. */
const FAILURE_LINE: Record<string, string> = {
  cancelled: "로그인을 그만두었습니다.",
  // ⚠️ **이 줄만 "무엇을 받는가"에 매여 있어 미결이 하나 붙어 있다**(PRD `7.13` D-65).
  // 카카오는 실명을 주지 않고 닉네임을 주며 이메일은 선택 동의라 안 올 수 있다
  // (`apps/api/src/lib/oauth.ts`의 `KakaoOAuthProvider.requestProfile`). **지금 카카오
  // 갈래는 이 줄에 닿지 않는다** — 그쪽 provider가 `PROFILE_DENIED`를 던지지 않기 때문이다.
  profile: "이름과 이메일을 받지 못해 계정을 만들 수 없습니다. 두 가지만 받습니다.",
  error: "로그인이 지금 되지 않습니다. 잠시 뒤 다시 눌러 주세요.",
};

/** 꺼진 버튼의 사유 (PRD `7.6.1` 확정, 2026-09-10. `A-01`도 같은 한 문장이다).
 *
 * ⚠️ **꺼진 것을 이름으로 부른다. 살아 있는 것을 세지 않는다.** 옛 문장은
 * `지금은 네이버 계정으로만 가입할 수 있습니다`였고, 그것이 깨진 원인은 카카오가 아니라
 * **살아 있는 목록을 문장 안에 박아 둔 것**이었다. 꺼진 쪽을 부르면 살아 있는 버튼이 몇
 * 개가 되든 참이고, **Apple을 켜는 날 이 줄은 고치는 것이 아니라 사라진다.**
 *
 * ⭐ **그 "사라진다"를 손이 아니라 목록이 한다** (2026-10-03). Apple 서버 구현은 들어와 있지만
 * 법무 `G1-30` 이 닫히기 전이라 `WEB_ENABLED_IDENTITY_PROVIDERS` 에 `APPLE` 이 없다. 아래
 * `APPLE_ENABLED` 가 그 목록에서 파생되므로 **켜는 날 목록 한 줄로 버튼이 살고 이 줄이 걷힌다.**
 *
 * `아직`은 시점 약속이 아니다. 금지된 것은 `곧`·`다음 업데이트에`처럼 지키지 못하면
 * 거짓말이 되는 말이고, Apple 일정은 App Store 등록에 매여 있어 이 화면이 모른다. */
const DISABLED_REASON = "Apple 계정으로는 아직 가입할 수 없습니다";

/** 사유 줄을 띄울지. **버튼과 같은 목록에서 파생한다** — 둘이 따로 놀면 살아 있는 버튼 밑에
 *  "아직 가입할 수 없습니다"가 남는다. */
const APPLE_ENABLED = WEB_ENABLED_IDENTITY_PROVIDERS.includes("APPLE");

/** 약관 잔글씨 안의 링크. 색과 밑줄은 잔글씨(`.terms` · `.terms u`)를 따른다. */
const TERMS_LINK = { color: "inherit", textDecoration: "none" } as const;

/** 로그인 버튼 하나. **어느 버튼이 사는지를 이 화면이 정하지 않는다.**
 *
 * `WEB_ENABLED_IDENTITY_PROVIDERS` 하나만 보고, 거기 없으면 꺼진 버튼이다. ⚠️ 앱이 읽는
 * `ENABLED_IDENTITY_PROVIDERS` 와 다른 목록이다. Apple은 앱과 웹 모두 같은 Services ID
 * 브라우저 OAuth를 쓰고, **켜는 PR 은 두 목록에 `APPLE` 을 함께 넣는다. 웹 목록에만 먼저
 * 넣지 않는다**(웹에서만 Apple로 가입한 셀러가 쓸 탈퇴 경로가 없다. `L-C32`,
 * `packages/shared/src/auth.ts`). 서버도 웹 시작 요청을 같은 목록으로 연다. **버튼마다
 * `disabled`를 손으로 적지 않는다** — 적으면 서버가 라우트를 연 것과 화면이 버튼을 켠 것이
 * 두 벌이 되고, 어긋나면 셀러는 눌리는 버튼이 실패하거나 되는 로그인을 못 본다.
 * `apps/mobile/src/app/login.tsx`의 `press()`가 같은 일을 한다.
 *
 * ⚠️ **살아 있는 것과 꺼진 것의 태그가 다르다.** 사는 쪽은 `<a>`(fetch가 아니라 최상위
 * 이동이다. 공급자 화면으로 나갔다가 돌아온다), 꺼진 쪽은 `disabled`를 걸 수 있는
 * `<button>`이다. **`<a>`에는 `disabled`가 없어서** 링크로 꺼진 버튼을 그리면 "눌리는데
 * 아무 일도 안 하는 것"이 아니라 **눌러서 없는 주소로 가는 것**이 된다.
 *
 * 낱말은 목업 `W-01` 그대로이고, 공급자 이름만 `IDENTITY_PROVIDER_LABEL`에서 꺼낸다.
 * 그 표가 `Record<IdentityProvider, string>`이라 **공급자를 늘리면서 낱말을 빠뜨리면
 * 컴파일에서 막힌다.** 화면 파일이 제공자별 낱말표를 두 번째로 만드는 것을 막는 자리다. */
function SocialStart({
  provider,
  verb,
  brand,
  from,
}: {
  provider: IdentityProvider;
  /** `시작하기` 또는 `계속하기`. **목업이 Apple만 다르게 적는다.** */
  verb: string;
  /** `styles/components.css`의 `.btn.login.{naver,kakao,apple}`. 브랜드 규정 색이다. */
  brand: string;
  from?: string;
}) {
  const label = `${IDENTITY_PROVIDER_LABEL[provider]}로 ${verb}`;
  const className = `btn login ${brand}`;

  if (!WEB_ENABLED_IDENTITY_PROVIDERS.includes(provider)) {
    return (
      <button type="button" className={className} disabled>
        {label}
      </button>
    );
  }
  return (
    <a className={className} href={loginStartUrl(provider, from)}>
      {label}
    </a>
  );
}

export function LoginScreen() {
  const { session, loading, accountChanged } = useSession();
  // `W-01` 과 `W-01b` 는 **같은 화면**이다. 로그인 상자만 바뀌고 왼쪽 설명과 주소는 그대로다
  // (목업 `W-01b` 캡션). 그래서 주소를 따로 두지 않고 상태 하나로 가른다.
  const [byId, setById] = useState(false);
  // 상자가 바뀌면 포커스를 새 상자의 첫 자리로 옮긴다. 안 옮기면 키보드 · 화면 낭독기 사용자의
  // 포커스가 사라진 버튼과 함께 문서 맨 앞으로 떨어진다. 아이디 상자 쪽은 `IdLoginBox` 가 아이디
  // 칸을 잡고, 돌아올 때는 들어갔던 문(`다른 방법으로 로그인`)으로 돌려놓는다.
  const altLoginRef = useRef<HTMLButtonElement>(null);
  const returningFromId = useRef(false);
  useEffect(() => {
    if (!byId && returningFromId.current) {
      returningFromId.current = false;
      altLoginRef.current?.focus();
    }
  }, [byId]);
  useDocumentTitle("로그인 · makit");
  const [params] = useSearchParams();
  const location = useLocation();

  // 게이트가 데려온 주소. 없으면 로그인 뒤 내 작업으로 간다. **서버가 허용 목록으로 한 번 더
  // 거른다** — 이 값은 주소창에서 만들어질 수 있어 여기서 거르는 것만으로는 부족하다.
  const from = (location.state as { from?: string } | null)?.from;
  const failure = FAILURE_LINE[params.get(LOGIN_REASON_PARAM) ?? ""];

  // 이미 로그인한 셀러에게 로그인 화면을 보이지 않는다. **앞 셀러와 다른 셀러가 들어왔으면
  // `from` 을 버린다.** 그 주소는 앞 셀러가 보던 자리다(`loginTarget`).
  if (!loading && session.authenticated) {
    return <Navigate to={loginTarget(from, accountChanged)} replace />;
  }

  return (
    <div className="wrap">
      <div className="shell">
        {/* 이 화면에 브랜드마크는 하나다. 셸 상단 줄은 두지 않는다 — 탭이 없어 브랜드만 이고
            있는 빈 줄이 되고, 같은 화면의 같은 로고 둘 중 어느 것이 머리인지 알 수 없다.
            서비스명은 마크 오른쪽 한 줄에 둔다(PRD 7.5.10 · 7.7.1). */}
        <div className="login">
          <div>
            <span className="loginbrand">
              <span className="brandmark" aria-hidden="true">
                <BrandMark />
              </span>
              <span className="wordmark-lg">makit</span>
            </span>
            <h1>
              포토샵 없이
              <br />
              상품상세를 만듭니다
            </h1>
            <p className="lead">
              사진을 올리면 배경을 지우고 정해진 틀에 맞춰 배치합니다. 스마트스토어 규격 그대로 나옵니다.
            </p>
            <ul className="pts">
              {/* **셀러가 이 제품에서 처음 보는 금액이다.** 로그인 전 화면이라 여기 적힌 값으로
                  쓸지 말지를 정한다. 두 가지를 2026-09-13에 고쳤다.
                  ① 단가를 앞에 둔다. 청구 단위가 **장**이고 권장 장수는 권장일 뿐 최소도
                     상한도 아니다. 그 장수부터 시작하는 줄로 읽히면 안 된다.
                  ② `약` 을 뺐다. 그 낱말은 원가가 환율을 타던 시절의 것이고, 청구가는 확정된
                     정수다. 확정값에 `약` 을 붙이면 셀러는 더 낼 수도 있다고 읽는다. */}
              <li>
                사진 1장 <span className="mono">{formatKrw(CUTOUT_UNIT_PRICE_KRW)}</span> ·{" "}
                {RECOMMENDED_IMAGE_COUNT}장이면{" "}
                <span className="mono">{formatKrw(cutoutPrice(RECOMMENDED_IMAGE_COUNT))}</span>{" "}
                · {VAT_INCLUDED}
              </li>
              <li>블록별 파일로 내보내 스마트에디터에 그대로 업로드</li>
              <li>폰에서 찍어 올리고 여기서 이어받기</li>
            </ul>
          </div>

          {byId ? (
            <IdLoginBox
              onBack={() => {
                returningFromId.current = true;
                setById(false);
              }}
            />
          ) : (
          <div className="loginbox">
            <h2>시작하기</h2>

            {/* 버튼 줄 **위**다. 아래에 두면 다시 누를 버튼이 사유보다 먼저 눈에 들어온다. */}
            {failure ? (
              <p className="loginfail" role="alert">
                {failure}
              </p>
            ) : null}

            <SocialStart provider="NAVER" verb="시작하기" brand="naver" from={from} />
            {/* **카카오는 목업이 2026-09-08에 더한 버튼이고 2026-09-10에 살아났다**
                (`A-01`도 같다). 지금 타깃만 보면 새로 닿는 셀러가 없지만 — 스마트스토어는
                네이버 계정 없이 운영할 수 없다 — 판매처가 늘면 그 자리를 답한다. 나중에
                더하지 않고 지금 켜는 이유는 **계정 정체성이 뒤에 옮기기 가장 비싼 것**이기
                때문이다. 셀러가 쌓은 작업과 결제가 계정에 묶여 있어서, 제공자를 늘리는 일이
                아니라 **이미 갈라진 계정을 합치는 일**이 된다. */}
            <SocialStart provider="KAKAO" verb="시작하기" brand="kakao" from={from} />
            {/* ⚠️ Apple만 `계속하기`다. 목업 `W-01`·`A-01`이 그렇게 적고 있다. */}
            <SocialStart provider="APPLE" verb="계속하기" brand="apple" from={from} />
            {/* **줄은 하나다** — 꺼진 버튼이 여럿이어도 한 문장 안에서 이름을 나열하고
                줄을 늘리지 않는다. 사유가 같은데 줄을 둘 두면 셀러는 다른 이유라고 읽는다. */}
            {APPLE_ENABLED ? null : <p className="loginwhy">{DISABLED_REASON}</p>}

            {/* ⭐ **뒷문장이 확정과 정반대를 말하고 있었다** (2026-09-24. `13.1` O-38·O-19
                확정 「학습 동의를 켜는 자리는 마이페이지 하나」). 옛 문장 `쓰일 수 있으며 …
                언제든 끌 수 있습니다`는 **기본이 켜짐**이라는 뜻이라, **기본이 꺼짐으로
                확정된 뒤로 참이 아니다** — 쓰이지 않고, 끌 것도 없다.
                ⛔ **더 나쁜 것은 자리였다.** `계속하면 동의하게 됩니다` 바로 뒤에 붙어
                **여기서 학습 동의를 받는 것처럼** 읽혔다.
                ⛔ **권유를 붙이지 않는다.** `켜 주시면`·`도움이 됩니다`를 적으면 가입
                자리에서 설득하는 것이 되고, **편익이 붙는 순간 대가 없는 동의가 아니게
                된다**(PRD `7.6.1`).
                ⚠️ **첫 문장(`개인정보처리방침에 동의`)은 이 회차가 손대지 않았다.** 밑줄을
                셋으로 늘리는 축이라 `7.13` D-53에 그대로 남는다.
                **정본은 목업 `W-01`이고 `scripts/screen-wording.test.mjs`가 맞대 본다.** */}
            {/* ⭐ **두 낱말은 밑줄 링크다**(PRD `7.6.1` 「밑줄 링크 2개」). 가는 곳은 로그인 없이
                열리는 `P-03` · `P-04` 이고 주소는 `PUBLIC_PATHS` 에서만 읽는다. 생김새는 `.terms u`
                가 정하므로 링크 자체의 기본 색·밑줄을 걷고 `<u>` 를 그대로 둔다. */}
            <p className="terms">
              {/* 낱말과 조사 사이에 줄바꿈을 두지 않는다. JSX 가 그 자리를 붙여 주지만 문장을
                  소스에서 떠 목업과 맞대는 시험(`screen-wording`)은 공백으로 읽는다. */}
              계속하면 <Link to={PUBLIC_PATHS.terms} style={TERMS_LINK}><u>이용약관</u></Link>과 <Link to={PUBLIC_PATHS.privacy} style={TERMS_LINK}><u>개인정보처리방침</u></Link>에 동의하게 됩니다. 올리신 사진은 품질 개선에 쓰지
              않습니다. 마이페이지에서 켜시면 그 뒤에 올리는 사진부터 씁니다.
            </p>

            {/* `W-01` 맨 아래 잔글씨 하나(2026-10-03). 결제사 심사자가 `W-01b` 로 가는 문이다.
                **버튼이 아니라 잔글씨**로 둔다. 버튼으로 두면 셀러가 넷째 로그인 수단으로 읽는다.
                링크 글에 `아이디` 도 `가입` 도 넣지 않는다(PRD `7.5.9`). 주소가 바뀌지 않는 동작이라
                태그는 `<button>` 이고 생김새만 잔글씨 링크다. */}
            <button type="button" className="altlogin" ref={altLoginRef} onClick={() => setById(true)}>
              다른 방법으로 로그인
            </button>
          </div>
          )}

          {/* 화면 아래의 설계 메모 상자(`CantDo`)를 걷었다 (2026-10-13). 목업 `W-01` 에서 그
              문장은 프레임 안이 아니라 프레임 캡션(`.phone-cap` 의 `.cant`)에 있다. 셀러가 할
              일은 위 상자(버튼 · 사유 줄 · 약관 문장 · 다른 방법으로 로그인)와 맨 아래 사업자
              정보(`App.tsx` 의 `BusinessInfo`)로 다 있다. */}
        </div>
      </div>
    </div>
  );
}

/** 아이디 로그인이 실패했을 때의 문장. **화면이 고르는 갈래는 서버의 상태 코드다.**
 *
 * - `mismatch` 는 목업 `W-01c` 글자 그대로다. **어느 칸이 틀렸는지 가르지 않는다.**
 * - `locked` · `off` 는 목업에 없다(`W-01c` 캡션이 「잠겼을 때의 문장」을 미정으로 뒀다). 이 회차가
 *   짧게 정했다. 둘 다 **할 수 있는 일**을 말한다. 잠긴 것은 기다리면 되고, 꺼진 것은 이 길로는
 *   할 일이 없다. `심사용 계정` 이라는 말은 쓰지 않는다(그 낱말은 문서와 운영의 말이다).
 *   ⛔ 얼마나 기다리면 되는지 적지 않는다. 잠금 시간은 서버가 정하고 이 화면은 모른다.
 * - `error` 는 소셜 로그인의 오류 줄(`FAILURE_LINE.error`)과 같은 문장이다. 할 수 있는 일이
 *   같아서 문장을 둘로 만들지 않는다. */
const ID_LOGIN_FAILURE: Record<Exclude<ReviewLoginResult, "ok">, string> = {
  mismatch: "아이디나 비밀번호가 맞지 않습니다.",
  locked: "여러 번 맞지 않아 잠시 로그인을 막았습니다. 잠시 뒤 다시 해 주세요.",
  off: "이 방법으로는 지금 로그인할 수 없습니다.",
  error: FAILURE_LINE.error as string,
};

/** `W-01b` 아이디로 로그인 · `W-01c` 맞지 않을 때.
 *
 * 칸은 **아이디 · 비밀번호 · 로그인** 셋뿐이다. 가입 · 비밀번호 찾기 · 로그인 유지를 두지 않고,
 * 셀러에게 이 길을 권하는 말도 두지 않는다(PRD `7.0`). 들어온 뒤의 권한은 셀러 한 명과 같다.
 *
 * 맞지 않으면 **아이디는 남기고 비밀번호 칸만 비우고, 두 칸 테두리를 함께** 빨갛게 한다
 * (목업 `W-01c`). 한 칸만 빨개지면 어느 쪽이 틀렸는지를 알려 준 셈이다.
 *
 * 성공하면 직접 이동하지 않는다. 세션을 다시 물어 로그인했는지를 확인하고, 그 뒤의 이동은
 * `LoginScreen` 의 기존 줄(`loginTarget`. `from` 또는 `/jobs`)이 한다. **「로그인했는가」를 판단하는 자리는
 * 하나여야 한다**(`Shell.tsx` 의 로그아웃과 같은 규율). */
function IdLoginBox({ onBack }: { onBack: () => void }) {
  const { refresh } = useSession();
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [failure, setFailure] = useState<Exclude<ReviewLoginResult, "ok"> | null>(null);
  const [pending, setPending] = useState(false);
  const loginIdRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  // 「다른 방법으로 로그인」을 눌러 이 상자가 서면 아이디 칸에서 시작한다.
  useEffect(() => {
    loginIdRef.current?.focus();
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending) return;
    setPending(true);
    setFailure(null);
    try {
      const result = await reviewLogin(loginId, password);
      if (result !== "ok") {
        setPassword("");
        // 비운 칸으로 포커스를 옮긴다. 다시 칠 자리가 거기다. 칸은 `pending` 동안에도 꺼지지 않아
        // 지금 바로 잡힌다.
        passwordRef.current?.focus();
        setFailure(result);
        return;
      }
      // 200 을 받았는데 세션이 안 서 있으면(쿠키가 안 실렸다) 셀러는 아무 일도 안 일어난 화면을
      // 본다. 그래서 한 번 물어 확인하고, 안 섰으면 오류 줄을 띄운다.
      // 이 질문이 끊기거나 5xx 면 던진다(`fetchSession`). 로그인은 됐을 수 있지만 셀러가 볼 수 있는
      // 것은 오류 줄 하나라 같은 줄로 받는다.
      const next = await fetchSession().catch(() => ANONYMOUS_SESSION);
      if (next.authenticated) {
        refresh();
      } else {
        setFailure("error");
      }
    } finally {
      setPending(false);
    }
  }

  const mismatch = failure === "mismatch";
  const fieldClass = mismatch ? "field bad" : "field";

  return (
    <div className="loginbox">
      <h2>아이디로 로그인</h2>
      <form className="idform" onSubmit={(event) => void submit(event)} noValidate>
        <label>
          아이디
          <input
            ref={loginIdRef}
            className={fieldClass}
            type="text"
            name="username"
            autoComplete="username"
            autoCapitalize="off"
            spellCheck={false}
            value={loginId}
            onChange={(event) => setLoginId(event.target.value)}
            aria-invalid={mismatch || undefined}
          />
        </label>
        <label>
          비밀번호
          <input
            ref={passwordRef}
            className={fieldClass}
            type="password"
            name="password"
            autoComplete="current-password"
            placeholder="비밀번호"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            aria-invalid={mismatch || undefined}
          />
        </label>
        {failure ? (
          <span className="err" role="alert">
            {ID_LOGIN_FAILURE[failure]}
          </span>
        ) : null}
        <button type="submit" className="btn" disabled={pending || !loginId || !password}>
          로그인
        </button>
      </form>
      <button type="button" className="altlogin" onClick={onBack}>
        ‹ 네이버 · 카카오로 시작하기
      </button>
    </div>
  );
}
